<!--
  Os encerramentos da rede (chamado NS 2026-0082, entrega 4; Full): os a conferir, os sem unidade
  da base (para casar) e todos, cada um com quantos itens faltam. "Abrir" leva ao checklist.
  Abre por eventBus.emit('modal.rede-encerramentos.open', { filtro: 'a_conferir' | 'para_casar' | '' });
  recarrega quando um encerramento é gravado ('rede.situacao.atualizada').
-->
<template>
  <Modal v-if="isVisible" :title="t('Encerramentos')" :visible="isVisible" :closable="true" :style="{ width: '960px', maxWidth: '96vw' }" data-testid="rede-encerramentos-modal" @update:visible="fechar">
    <div class="filtros">
      <app-button v-for="f in FILTROS" :key="f.chave" :label="`${t(f.rotulo)} (${contagem(f.chave)})`" size="small" :severity="filtro === f.chave ? undefined : 'secondary'"
                  :data-testid="`rede-encerramentos-filtro-${f.chave || 'todos'}`" @click="filtro = f.chave" />
    </div>
    <p v-if="!lista" class="quieto">{{ t('Carregando…') }}</p>
    <data-table v-else data-testid="rede-encerramentos-tabela" :config="tabela" :data="linhas" @button-click="aoClicar" />
  </Modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRede, mensagemDaRede } from '../../composables/useRede';
import { eventBus } from '../../eventBus';
import type { Rede_EncerramentosResponseDados } from '../../contracts/Rede_EncerramentosResponse';
import type { Rede_EncerramentoRow } from '../../contracts/Rede_EncerramentoRow';

const { t } = useI18n();
const { carregarEncerramentos } = useRede();

const FILTROS = [
  { chave: 'a_conferir', rotulo: 'A conferir' },
  { chave: 'para_casar', rotulo: 'Para casar' },
  { chave: '', rotulo: 'Todos' },
];
const ORIGENS: Record<string, string> = { planilha: 'Planilha', juridico: 'Jurídico', manual: 'Tela' };

const isVisible = ref(false);
const filtro = ref('');
const lista = ref<Rede_EncerramentosResponseDados | null>(null);

const emAndamento = (e: Rede_EncerramentoRow): boolean => e.situacao === 'em_andamento';
const passa = (e: Rede_EncerramentoRow, chave: string): boolean => {
  if (chave === 'a_conferir') return emAndamento(e) && e.a_conferir === true;
  if (chave === 'para_casar') return emAndamento(e) && !e.unidade;
  return true;
};
const contagem = (chave: string): number => (lista.value?.encerramentos ?? []).filter((e) => passa(e, chave)).length;
const dataBr = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '');
const escapar = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));

type Linha = Rede_EncerramentoRow & { origem_texto: string; situacao_texto: string };
const linhas = computed<Linha[]>(() =>
  (lista.value?.encerramentos ?? [])
    .filter((e) => passa(e, filtro.value))
    .map((e) => ({
      ...e,
      origem_texto: t(ORIGENS[e.origem] ?? e.origem),
      situacao_texto: emAndamento(e)
        ? `${e.pendentes} ${t('pendentes')}`
        : `${t(e.destino === 'repassada' ? 'Repassada' : 'Encerrada')} ${dataBr(e.concluido_em)}`,
    })),
);

const tabela = computed(() => ({
  columns: [
    {
      data: 'nome',
      title: t('Unidade'),
      sortable: true,
      cellRenderer: ({ data }: { data: Linha }): string =>
        escapar(data.nome ?? '') + (data.unidade ? '' : ` <span style="color:#9a6700;font-size:12px">${escapar(t('(sem unidade da base)'))}</span>`),
    },
    { data: 'origem_texto', title: t('Origem'), width: 110, sortable: true },
    { data: 'situacao_texto', title: t('Situação'), width: 170, sortable: true },
    {
      data: 'divergencia',
      title: t('A conferir'),
      sortable: false,
      cellRenderer: ({ data }: { data: Linha }): string =>
        data.a_conferir && emAndamento(data) ? `<span style="color:#9a6700">${escapar(data.divergencia ?? t('Sim'))}</span>` : '',
    },
    {
      data: 'id',
      title: t('Ações'),
      width: 110,
      sortable: false,
      renderType: 'action_buttons',
      buttons: [{ text: t('Abrir'), icon: 'pi pi-list-check', class: 'p-button p-button-secondary p-button-sm', action: 'rede-encerramento' }],
    },
  ],
  pagination: true,
  pageLength: 25,
  stripedRows: true,
  showGridlines: true,
  size: 'small',
}));

const recarregar = (): void => {
  if (!isVisible.value) return;
  carregarEncerramentos()
    .then((l) => {
      lista.value = l;
    })
    .catch((e: unknown) => eventBus.emit('globalMessage.error', t(mensagemDaRede(e))));
};

const abrir = (dados?: { filtro?: string }): void => {
  filtro.value = dados?.filtro ?? '';
  lista.value = null;
  isVisible.value = true;
  recarregar();
};

const fechar = (): void => {
  isVisible.value = false;
};

const aoClicar = ({ data, action }: { data: Linha; action: string }): void => {
  if (action === 'rede-encerramento') eventBus.emit('modal.rede-encerramento.open', { id: data.id });
};

eventBus.on('modal.rede-encerramentos.open', abrir);
eventBus.on('rede.situacao.atualizada', recarregar);
onBeforeUnmount(() => {
  eventBus.off('modal.rede-encerramentos.open', abrir);
  eventBus.off('rede.situacao.atualizada', recarregar);
});
</script>

<style scoped>
.filtros { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
.quieto { color: #5b6168; }
</style>
